import { FC, useMemo, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { RegisteredAnimalTarget, SampleDefinitionApiId } from '@/model/SamplePlan.model.ts';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model.ts';
import APITable from '@/components/UI/Table/Reusable/APITable';
import { RouteService } from '@/support/RouteService.ts';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import { animalFilterOptions, AnimalForSampling, calculateNumberOfSamples } from './AnimalSelectionTable.utils.ts';
import { StudyApiId } from '@/model/Study.model.ts';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash.ts';
import { ApiInfo, TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model.ts';
import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers.tsx';

// No data component for APITable
const NoDataComponent: FC = () => (
  <div className="bg-white ba b--black-10 br2 pa4 tc">
    <p className="i gray">No animals available for the selected sample definitions.</p>
  </div>
);

const columns: Array<TanStackColumnDef<AnimalForSampling>> = [
  {
    id: 'number',
    header: 'Animal no.',
    accessorKey: 'number',
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'name',
    header: 'Animal name',
    accessorKey: 'name',
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'cage_name',
    header: 'Cage name',
    isVisible: true,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: ({ row }: { row: { original: AnimalForSampling } }) => row.original.cage?.name || '',
  },
  {
    id: 'study_group',
    header: 'Study group',
    isVisible: true,
    enableSorting: true,
    cell: ({ row }: { row: { original: AnimalForSampling } }) => {
      const group = row.original.study_group;
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      return group ? <GroupLabel group={group} /> : 'Unassigned';
    },
  },
  {
    id: 'planned_sample_count',
    header: 'No. of planned samples',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: AnimalForSampling } }) => calculateNumberOfSamples(row.original.sample_groups),
  },
  {
    id: 'planned_sample_group',
    header: 'Name',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: AnimalForSampling } }) =>
      row.original.sample_groups.map((group: { name: string }) => group.name).join(', '),
  },
  {
    id: 'sex',
    header: 'Sex',
    accessorKey: 'sex',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AnimalForSampling } }) => {
      if (_notNil(original.sex)) {
        return original.sex === 'f' ? 'Female' : 'Male';
      }
      return '';
    },
  },
  {
    id: 'deceased',
    header: 'Deceased date',
    accessorKey: 'terminated_at',
    isVisible: false,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: AnimalForSampling } }) => (
      <DateRenderer value={original.terminated_at} defaultResponse="" />
    ),
  },
];

interface AnimalSelectionTableProps {
  studyApiId: StudyApiId;
  onSelectionChange: (selectedAnimals: Set<RegisteredAnimalTarget['api_id']>) => void;
  sampleDefinitionIds?: Array<SampleDefinitionApiId>;
  onAnimalsDataChange?: (animals: Array<AnimalForSampling>) => void;
  collectionType: 'planned' | 'collected';
  defaultFilters?: Array<TableFilter>;
}

export const AnimalSelectionTable: FC<AnimalSelectionTableProps> = ({
  studyApiId,
  onSelectionChange,
  sampleDefinitionIds: propSampleDefinitionIds,
  onAnimalsDataChange,
  collectionType,
  defaultFilters,
}) => {
  const { id: studyId } = useParams();
  const location = useLocation();

  // Create API info for APITable
  const { apiInfo, sampleDefinitionIds } = useMemo<{
    apiInfo: ApiInfo | null;
    sampleDefinitionIds: Array<SampleDefinitionApiId>;
  }>(() => {
    const defApiIds: Array<SampleDefinitionApiId> = _isNotEmpty(propSampleDefinitionIds)
      ? [...propSampleDefinitionIds]
      : ((location.state as { sampleDefinitionIds?: Array<SampleDefinitionApiId> })?.sampleDefinitionIds ?? []);

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!studyApiId) {
      return { apiInfo: null, sampleDefinitionIds: defApiIds };
    }
    return {
      apiInfo: {
        type: 'internalApi' as const,
        route: RouteService.api({
          endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/animals',
          path: { studyApiId: studyApiId as StudyApiId },
          query: {
            ...(_isEmpty(defApiIds) ? {} : { definitions: defApiIds }),
            collection_type: collectionType,
          },
        }).url,
      },
      sampleDefinitionIds: defApiIds,
    };
  }, [studyApiId, collectionType, propSampleDefinitionIds]);

  // State for animals data
  const [animals, setAnimals] = useState<Array<AnimalForSampling>>([]);

  // Filter options that update when animals data changes
  const filterOptions = useMemo(() => {
    return animalFilterOptions(animals, studyId);
  }, [animals, studyId]);

  // Callback for when animals data is loaded from API
  const handleFetchTableData = useCallback(
    (animals: Array<AnimalForSampling>) => {
      // Filter animals to only include those with sample definitions in their sample_groups
      const filteredAnimals = animals.filter((animal) => {
        if (_isEmpty(sampleDefinitionIds)) {
          return true;
        }
        // Check if this animal has any of the selected sample definitions in its sample_groups
        return animal.sample_groups?.some((group) => sampleDefinitionIds.includes(group.api_id)) ?? false;
      });

      setAnimals(filteredAnimals);
      // Pass filtered animals data back to parent component if callback provided
      if (_notNil(onAnimalsDataChange)) {
        onAnimalsDataChange(filteredAnimals);
      }
    },
    [onAnimalsDataChange, sampleDefinitionIds]
  );

  // Handle row selection through APITable's onSelectRow
  const handleSelectRow = useCallback(
    (selectedFlatRows: Array<unknown>) => {
      const typedRows = selectedFlatRows as Array<{ original: AnimalForSampling }>;
      const selectedApiIds = new Set(typedRows.map((row) => row.original.api_id));
      onSelectionChange(selectedApiIds);
    },
    [onSelectionChange]
  );

  // Don't render if no studyApiId or no API info
  if (_isNil(studyApiId) || _isNil(apiInfo) || _isEmpty(sampleDefinitionIds.length)) {
    return (
      <div className="bg-white ba b--black-10 br2 pa4 tc">
        <p className="i gray">No animals available for the selected sample definitions.</p>
      </div>
    );
  }

  return (
    <div className="bg-white ba b--black-10 br2">
      <APITable<AnimalForSampling>
        columns={columns}
        apiInfo={apiInfo}
        onSelectRow={handleSelectRow}
        NoDataComponent={NoDataComponent}
        filterOptions={filterOptions}
        searchPlaceholderText="Search by Name"
        testIdPrefix="AnimalSelection"
        defaultSortBy={{ id: 'number', desc: false }}
        defaultPageSize={25}
        maxHeight={400}
        noDataMessage="No animals available for the selected sample definitions."
        onFetchTableData={handleFetchTableData}
        selectAllOnLoad={true}
        defaultFilters={defaultFilters}
      />
    </div>
  );
};
